import React from 'react'
import Tooltip from '@mui/material/Tooltip'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import { SpeakerWaveIcon } from '@heroicons/react/24/outline'

import { ARIA_INTERACTION_MODES, AriaInteractionMode } from '../../../../pure-js/types/AppAssistant'
import { AssistantUiKey } from '../../../../pure-js/libs/assistantMessages'
import { TESTIDS } from '../../config/testids'
import { buildAriaPillToggleGroupSx } from '../coachPreferences/coachSurfaceStyles'
import { useTranslation } from '../hooks/useIntractionTranslate'
import { CaptionsIcon } from './icons/CaptionsIcon'
import { KeyboardIcon } from './icons/KeyboardIcon'

const MODE_ICONS: Record<AriaInteractionMode, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
  [AriaInteractionMode.VOICE]: SpeakerWaveIcon,
  [AriaInteractionMode.VOICE_WITH_SUBTITLES]: CaptionsIcon,
  [AriaInteractionMode.TEXT]: KeyboardIcon
}

const MODE_LABEL_KEYS: Record<AriaInteractionMode, AssistantUiKey> = {
  [AriaInteractionMode.VOICE]: AssistantUiKey.MODE_VOICE,
  [AriaInteractionMode.VOICE_WITH_SUBTITLES]: AssistantUiKey.MODE_VOICE_SUBTITLES,
  [AriaInteractionMode.TEXT]: AssistantUiKey.MODE_TEXT
}

const MODE_TESTIDS: Record<AriaInteractionMode, string> = {
  [AriaInteractionMode.VOICE]: TESTIDS.ARIA_MODE_VOICE_BTN,
  [AriaInteractionMode.VOICE_WITH_SUBTITLES]: TESTIDS.ARIA_MODE_SUBTITLES_BTN,
  [AriaInteractionMode.TEXT]: TESTIDS.ARIA_MODE_TEXT_BTN
}

const ICON_SIZE_PX = 17
/** Sized so all three buttons plus the language picker fit the assistant header's single row. */
const BUTTON_PADDING_PX = 6

export interface AriaModeSwitchProps {
  mode: AriaInteractionMode
  onChange: (mode: AriaInteractionMode) => void
}

/** The three ways to talk to Aria. A segmented group so the current mode is visible at a glance. */
export const AriaModeSwitch: React.FC<AriaModeSwitchProps> = ({ mode, onChange }) => {
  const { t } = useTranslation()

  return (
    <ToggleButtonGroup
      exclusive
      value={mode}
      aria-label={t(AssistantUiKey.MODE_GROUP_LABEL)}
      data-testid={TESTIDS.ARIA_MODE_SWITCH}
      onChange={(_event, next: AriaInteractionMode | null) => {
        // MUI reports null when the active button is clicked again; keep the current mode.
        if (next) onChange(next)
      }}
      sx={buildAriaPillToggleGroupSx({ p: `${BUTTON_PADDING_PX}px` })}
    >
      {ARIA_INTERACTION_MODES.map((candidate) => {
        const Icon = MODE_ICONS[candidate]
        const label = t(MODE_LABEL_KEYS[candidate])
        return (
          <ToggleButton key={candidate} value={candidate} aria-label={label} data-testid={MODE_TESTIDS[candidate]}>
            <Tooltip title={label}>
              <Icon width={ICON_SIZE_PX} height={ICON_SIZE_PX} />
            </Tooltip>
          </ToggleButton>
        )
      })}
    </ToggleButtonGroup>
  )
}

export default AriaModeSwitch
